import {animate, style, transition, trigger} from '@angular/animations';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {Router} from '@angular/router';
import {FrontendDocument, FrontendStatusEnum, StatusEnum} from '@nachdigal';
import {TranslateService} from '@ngx-translate/core';
import {FragebogenTypPipe} from '../../../../fragebogen/fragebogen-typ.pipe';
import {PreviewDocumentService} from '@utils-lib';
import {LoaderService} from '../../../../shared/service/loader.service';

@Component({
    selector: 'frageboegen-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({transform: 'translateX(100%)'}),
                animate('300ms ease-in', style({transform: 'translateX(0%)'}))
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({transform: 'translateX(100%)'}))
            ])
        ])
    ]
})
export class DataTableComponent implements OnChanges {
    @Input() inputData: Array<FrontendDocument>;
    @Input() tableId: string;
    @Input() tableHeaders: TableHeader[];
    @Input() defaultSortColumnId: string;
    @Input() activeSearch: boolean;
    @Input() searchValue: string;
    @Input() sentDocumentId: string;
    @Input() selectedDocumentId: string;
    @Output() deleteItem = new EventEmitter<string>();
    @ViewChild('tableHeader') tableHeader;
    @ViewChild('tableRowContainer') tableRowContainer;
    public kebabOpen = false;
    public activeElementIndex: number;
    public tableData: TableData[];
    public activeDeleteDialog = false;
    public showTooltip = false;
    public toolTipText = '';
    private readonly fragebogenTypPipe: FragebogenTypPipe;

    constructor(
        private readonly translateService: TranslateService,
        private readonly router: Router,
        private readonly previewService: PreviewDocumentService,
        private readonly loadingService: LoaderService) {
        this.fragebogenTypPipe = new FragebogenTypPipe(this.translateService);
    }

    public findDocumentIndexById(array: Array<TableData>, id: string): number {
        let foundIndex = -1;
        array.forEach((item, index) => {
            if (item.id === id) {
                foundIndex = index;
            }
        });
        return foundIndex;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.inputData?.currentValue) {
            this.tableData = this.fillTableData(changes.inputData.currentValue);
            this.showTooltip = false;
            if (sessionStorage.getItem(this.tableId + '_sortOn')) {
                const header = this.tableHeaders?.find(x => x.id === sessionStorage.getItem(this.tableId + '_sortOn'));
                if (header) {
                    header.sortDirection = this.changeSortDirection(sessionStorage.getItem(this.tableId + '_sortDirection'));
                }
                this.onSort(header);
            } else {
                const defaultTableHeader = this.tableHeaders?.find(x => x.id === this.defaultSortColumnId);
                defaultTableHeader.sortDirection = this.changeSortDirection(defaultTableHeader.sortDirection);
            }
            setTimeout(() => {
                const doc = document.documentElement;
                let tableHeaderHeight = this.tableHeader?.nativeElement.offsetHeight;
                if (!tableHeaderHeight) {
                    const fixHeight = 40;
                    tableHeaderHeight = fixHeight;
                }
                doc.style.setProperty('--table-header-height', `${tableHeaderHeight}px`);
                this.setFocusToCurrentDocument();
            });
        }
    }

    public onSort(clickedHeader: TableHeader): void {
        clickedHeader.sortDirection = this.changeSortDirection(clickedHeader.sortDirection);
        this.tableHeaders = this.resetHeaders(clickedHeader.id, this.tableHeaders);
        this.tableData = this.sort(this.tableData, clickedHeader);
        sessionStorage.setItem(this.tableId + '_sortOn', clickedHeader.id);
        sessionStorage.setItem(this.tableId + '_sortDirection', clickedHeader.sortDirection);
    }

    public resetHeaders(idToExclude: string, tableHeaders: TableHeader[]): TableHeader[] {
        tableHeaders.forEach(header => {
            if (header.id !== idToExclude) {
                header.sortDirection = '';
            }
        });
        return tableHeaders;
    }

    public changeSortDirection(sortDirection: string): SortDirection {
        const rotate: { [key: string]: SortDirection } = {asc: 'desc', desc: 'asc', '': 'asc'};
        return rotate[sortDirection];
    }

    public sort(tableData: TableData[], clickedHeader: TableHeader): TableData[] {
        const compare = (v1: string | number, v2: string | number) => {
            if (v1 < v2) {
                return -1;
            } else if (v1 > v2) {
                return 1;
            } else {
                return 0;
            }
        };
        return tableData.sort((a, b) => {
            let firstColumn = a[clickedHeader.id];
            let secondColumn = b[clickedHeader.id];
            if (firstColumn !== null && typeof (firstColumn) === 'string') {
                firstColumn = firstColumn.toUpperCase();
            }
            if (secondColumn !== null && typeof (secondColumn) === 'string') {
                secondColumn = secondColumn.toUpperCase();
            }
            const res = compare(firstColumn, secondColumn);
            if (clickedHeader.id === 'transfer_date' || clickedHeader.id === 'last_changed_on') {
                return clickedHeader.sortDirection === 'asc' ? -res : res;
            }
            return clickedHeader.sortDirection === 'asc' ? res : -res;
        });
    }

    public fillTableData(documents: FrontendDocument[]): TableData[] {
        const dataList: TableData[] = [];
        if (documents) {
            for (const document of documents) {
                const data: TableData = {
                    id: document?.id,
                    client_name: document?.client_name,
                    master_client_number: +document?.element_of_order?.master_client_number,
                    consultant_number: +document?.element_of_order?.consultant_number,
                    app_name: this.fragebogenTypPipe.transform(document?.element_of_order?.app_name),
                    transfer_date: new Date(document?.transfer_date),
                    last_changed_on: new Date(document?.last_changed_on),
                    status: document?.status
                };
                dataList.push(data);
            }
        }
        return dataList;
    }

    public navigateToAntrag(id: string, $event: Event): void {
        $event.stopPropagation();
        if (($event.target as HTMLElement).classList[0] === 'data-row' ||
            ($event.target as HTMLElement).parentElement.classList[0] === 'data-row' ||
            ($event.target as HTMLElement).id === 'edit') {
            if (this.tableId === 'gesendet') {
                this.previewService.displayPdfData(id, true);
            } else {
                this.loadingService.isLoading = {isLoading: true};
                this.router.navigate([id]);
            }
        }
    }

    public onShowTooltip(index, status: StatusEnum): void {
        this.activeElementIndex = index;
        this.showTooltip = true;
        if (status === StatusEnum.SendingError) {
            this.toolTipText = this.translateService.instant('FRAGEBOEGEN_APP.UEBERSICHT.TOOLTIP_ERROR');
        } else if (status === StatusEnum.Sending) {
            this.toolTipText = this.translateService.instant('FRAGEBOEGEN_APP.UEBERSICHT.TOOLTIP_SENDING');
        } else {
            this.toolTipText = '';
        }
    }

    public onHideTooltip(): void {
        this.showTooltip = false;
    }

    public toggleDropdown(index: number, $event: Event): void {
        $event.preventDefault();
        $event.stopImmediatePropagation();
        this.kebabOpen = !this.kebabOpen || index !== this.activeElementIndex;
        this.activeDeleteDialog = false;
        this.activeElementIndex = index;
    }

    public showDeleteDialog(index: number, $event: Event): void {
        $event.stopPropagation();
        this.activeDeleteDialog = true;
        this.activeElementIndex = index;
        this.kebabOpen = false;
    }

    public onCancelDraft($event): void {
        this.activeDeleteDialog = false;
        $event.stopPropagation();
    }

    public onDeleteDraft(taskId: string, $event: Event): void {
        $event.stopPropagation();
        this.activeDeleteDialog = false;
        this.deleteItem.emit(taskId);
    }

    private setFocusToCurrentDocument(): void {
        const documentId = this.selectedDocumentId ? this.selectedDocumentId : this.sentDocumentId;
        if (documentId) {
            setTimeout(() => {
                const index = this.findDocumentIndexById(this.tableData, documentId);
                try {
                    this.tableRowContainer?.nativeElement.children[index].focus({preventScroll: false});
                    this.selectedDocumentId = null;
                    this.sentDocumentId = null;
                } catch (e) {
                }
            });
        }
    }
}

export interface TableData {
    id: string;
    consultant_number: number;
    master_client_number: number;
    client_name: string;
    app_name: string;
    transfer_date?: Date;
    last_changed_on?: Date;
    status?: FrontendStatusEnum;
}

export interface TableHeader {
    name: string;
    id: string;
    sortDirection?: SortDirection;
}

export type SortDirection = 'asc' | 'desc' | '';
